// /home/labdoo/zitech-nexus/frontend/src/Layout/DashboardLayout.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { authAPI, announcementAPI, adminAPI, jobsAPI, contactAPI, supportAPI, newsletterAPI, portfolioAPI, classMarksAPI, getCurrentUser, logout } from '../services/api';
import './DashboardLayout.css';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  created_at: string;
  link?: string;
  category?: string;
}

interface UserProfile {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: 'user' | 'admin' | 'superadmin';
  phone_number?: string;
  company_name?: string;
}

const DashboardLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [activeMenu, setActiveMenu] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      fetchUserProfile();
      fetchNotifications();
    } else {
      window.location.href = '/login';
    }
    
    const path = location.pathname;
    updateActiveMenu(path);
  }, [location]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch user profile from API
  const fetchUserProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  // Fetch real notifications from API
  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    const notificationsList: Notification[] = [];

    try {
      const currentUser = getCurrentUser();
      const isAdminUser = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
      const isSuperAdminUser = currentUser?.role === 'superadmin';

      // Fetch announcements (for all users)
      try {
        const response = await announcementAPI.getAll({ is_active: true });
        const announcements = response.data.results || response.data || [];
        if (Array.isArray(announcements) && announcements.length > 0) {
          announcements.forEach((ann: any, index: number) => {
            notificationsList.push({
              id: `announcement-${ann.id || index}`,
              title: ann.title || 'New Announcement',
              message: ann.content || ann.message || 'New announcement available',
              type: 'info',
              read: false,
              created_at: ann.created_at || new Date().toISOString(),
              link: '/dashboard/admin/announcements',
              category: 'announcement',
            });
          });
        }
      } catch (e) {
        console.log('Announcements not available');
      }

      // Fetch service requests (for admin/superadmin)
      if (isAdminUser) {
        try {
          const response = await adminAPI.getAllRequests();
          const requests = response.data.results || response.data || [];
          if (Array.isArray(requests) && requests.length > 0) {
            const pendingRequests = requests.filter((r: any) => r.status === 'pending');
            pendingRequests.slice(0, 5).forEach((req: any, index: number) => {
              notificationsList.push({
                id: `request-${req.id || index}`,
                title: 'New Service Request',
                message: `${req.user_name || 'A user'} submitted a service request`,
                type: 'info',
                read: false,
                created_at: req.created_at || new Date().toISOString(),
                link: '/dashboard/admin/requests',
                category: 'service_request',
              });
            });
          }
        } catch (e) {
          console.log('Service requests not available');
        }

        // Fetch job applications (for admin/superadmin)
        try {
          const response = await jobsAPI.getAllApplications();
          const applications = response.data.results || response.data || [];
          if (Array.isArray(applications) && applications.length > 0) {
            const newApps = applications.filter((app: any) => app.status === 'pending');
            newApps.slice(0, 5).forEach((app: any, index: number) => {
              notificationsList.push({
                id: `application-${app.id || index}`,
                title: 'New Job Application',
                message: `${app.applicant_name || 'Someone'} applied for a position`,
                type: 'success',
                read: false,
                created_at: app.applied_at || app.created_at || new Date().toISOString(),
                link: '/dashboard/admin/applications',
                category: 'job_application',
              });
            });
          }
        } catch (e) {
          console.log('Job applications not available');
        }

        // Fetch contact messages (for admin/superadmin)
        try {
          const response = await contactAPI.getAllMessages();
          const messages = response.data.results || response.data || [];
          if (Array.isArray(messages) && messages.length > 0) {
            const unreadMessages = messages.filter((m: any) => !m.is_read);
            unreadMessages.slice(0, 5).forEach((msg: any, index: number) => {
              notificationsList.push({
                id: `message-${msg.id || index}`,
                title: 'New Contact Message',
                message: `New message from ${msg.name || 'a visitor'}`,
                type: 'warning',
                read: false,
                created_at: msg.created_at || new Date().toISOString(),
                link: '/dashboard/admin/contact',
                category: 'contact_message',
              });
            });
          }
        } catch (e) {
          console.log('Contact messages not available');
        }

        // Fetch support tickets (for admin/superadmin)
        try {
          const response = await supportAPI.adminGetAllTickets();
          const tickets = response.data.results || response.data || [];
          if (Array.isArray(tickets) && tickets.length > 0) {
            const openTickets = tickets.filter((t: any) => t.status !== 'closed');
            openTickets.slice(0, 5).forEach((ticket: any, index: number) => {
              notificationsList.push({
                id: `ticket-${ticket.id || index}`,
                title: 'Support Ticket Update',
                message: `Ticket #${ticket.id}: ${ticket.subject || 'Support request'}`,
                type: 'error',
                read: false,
                created_at: ticket.updated_at || ticket.created_at || new Date().toISOString(),
                link: '/dashboard/admin/support/tickets',
                category: 'support_ticket',
              });
            });
          }
        } catch (e) {
          console.log('Support tickets not available');
        }
      }

      // Sort by date (newest first) and limit to 10
      notificationsList.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      const limitedNotifications = notificationsList.slice(0, 10);
      setNotifications(limitedNotifications);
      const unread = limitedNotifications.filter((n: Notification) => !n.read).length;
      setUnreadCount(unread);
      
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const markNotificationAsRead = async (notificationId: string) => {
    try {
      // Try to mark as read via API if available
      // Since we don't have a dedicated notifications API, we just update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );
    setUnreadCount(0);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markNotificationAsRead(notification.id);
    }
    if (notification.link) {
      setNotificationsOpen(false);
      navigate(notification.link);
    }
  };

  const getTimeAgo = (dateString: string) => {
    try {
      const now = new Date();
      const date = new Date(dateString);
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Recently';
    }
  };

  const updateActiveMenu = (path: string) => {
    if (path === '/dashboard/main' || path === '/dashboard') setActiveMenu('dashboard');
    else if (path === '/dashboard/service-requests') setActiveMenu('requests');
    else if (path === '/dashboard/request-service') setActiveMenu('new-request');
    else if (path === '/dashboard/profile') setActiveMenu('profile');
    else if (path === '/dashboard/settings') setActiveMenu('settings');
    else if (path === '/dashboard/trainings') setActiveMenu('trainings');
    else if (path === '/dashboard/my-applications') setActiveMenu('my-applications');
    else if (path === '/dashboard/class-marks') setActiveMenu('class-marks');
    else if (path === '/dashboard/class-marks/create') setActiveMenu('class-marks');
    else if (path.includes('/dashboard/class-marks/')) setActiveMenu('class-marks');
    else if (path === '/dashboard/admin/requests') setActiveMenu('admin-requests');
    else if (path === '/dashboard/admin/users') setActiveMenu('admin-users');
    else if (path === '/dashboard/admin/services') setActiveMenu('admin-services');
    else if (path === '/dashboard/admin/contact') setActiveMenu('admin-contact');
    else if (path === '/dashboard/admin/jobs') setActiveMenu('admin-jobs');
    else if (path === '/dashboard/admin/applications') setActiveMenu('admin-applications');
    else if (path === '/dashboard/admin/blog/posts') setActiveMenu('admin-blog');
    else if (path === '/dashboard/admin/blog/categories') setActiveMenu('admin-blog');
    else if (path === '/dashboard/admin/blog/comments') setActiveMenu('admin-blog');
    else if (path === '/dashboard/admin/support/tickets') setActiveMenu('admin-support');
    else if (path === '/dashboard/admin/newsletter') setActiveMenu('newsletter-dashboard');
    else if (path === '/dashboard/admin/newsletter/subscribers') setActiveMenu('subscribers');
    else if (path === '/dashboard/admin/newsletter/campaigns') setActiveMenu('campaigns');
    else if (path === '/dashboard/admin/newsletter/create') setActiveMenu('create-campaign');
    else if (path === '/dashboard/admin/newsletter/settings') setActiveMenu('newsletter-settings');
    else if (path === '/dashboard/admin/portfolio/settings') setActiveMenu('portfolio-settings');
    else if (path === '/dashboard/admin/announcements') setActiveMenu('announcements');
    else if (path === '/dashboard/admin/training/modules') setActiveMenu('training-management');
    else if (path === '/dashboard/admin/training/enrollments') setActiveMenu('training-management');
    else if (path === '/dashboard/admin/student-progress') setActiveMenu('student-progress');
    else if (path === '/dashboard/admin/portfolio/projects') setActiveMenu('portfolio-projects');
    else if (path === '/dashboard/admin/portfolio/solutions') setActiveMenu('portfolio-solutions');
    else if (path.includes('/dashboard/admin/portfolio/projects/')) setActiveMenu('portfolio-projects');
    else if (path.includes('/dashboard/admin/portfolio/solutions/')) setActiveMenu('portfolio-solutions');
    else if (path.includes('/dashboard/admin/training/')) setActiveMenu('training-management');
    else if (path.includes('/dashboard/admin/student-progress/')) setActiveMenu('student-progress');
    else if (path.includes('/service-request/')) setActiveMenu('requests');
    else if (path.includes('/training/')) setActiveMenu('trainings');
  };

  const handleLogout = () => {
    logout();
  };

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name.charAt(0).toUpperCase();
    }
    if (user?.username) {
      return user.username.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getFullName = () => {
    if (user?.full_name) {
      return user.full_name;
    }
    if (user?.username) {
      return user.username;
    }
    return 'User';
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
    if (notificationsOpen) setNotificationsOpen(false);
  };

  const toggleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
    if (dropdownOpen) setDropdownOpen(false);
  };

  const userRole = user?.role || 'user';
  const isSuperAdmin = userRole === 'superadmin';
  const isAdmin = userRole === 'admin';

  // Define menu items based on role
  const getMenuItems = () => {
    if (isSuperAdmin) {
      return [
        { id: 'dashboard', label: 'Dashboard', path: '/dashboard/main', icon: 'dashboard' },
        { id: 'my-applications', label: 'My Job Applications', path: '/dashboard/my-applications', icon: 'applications' },
        { id: 'admin-requests', label: 'All Requests', path: '/dashboard/admin/requests', icon: 'admin-requests' },
        { id: 'admin-users', label: 'User Management', path: '/dashboard/admin/users', icon: 'admin-users' },
        { id: 'admin-services', label: 'Service Management', path: '/dashboard/admin/services', icon: 'admin-services' },
        { id: 'admin-jobs', label: 'Jobs', path: '/dashboard/admin/jobs', icon: 'briefcase' },
        { id: 'admin-applications', label: 'Job Applications', path: '/dashboard/admin/applications', icon: 'applications' },
        { id: 'admin-blog', label: 'Blog Management', path: '/dashboard/admin/blog/posts', icon: 'blog' },
        { id: 'admin-contact', label: 'Contact Messages', path: '/dashboard/admin/contact', icon: 'message' },
        { id: 'admin-support', label: 'Support Tickets', path: '/dashboard/admin/support/tickets', icon: 'support' },
        { id: 'training-management', label: 'Training Management', path: '/dashboard/admin/training/modules', icon: 'learning' },
        { id: 'student-progress', label: 'Student Progress', path: '/dashboard/admin/student-progress', icon: 'student-progress' },
        { id: 'announcements', label: 'Announcements', path: '/dashboard/admin/announcements', icon: 'announcements' },
        { id: 'newsletter-dashboard', label: 'Newsletter Analytics', path: '/dashboard/admin/newsletter', icon: 'newsletter' },
        { id: 'subscribers', label: 'Subscribers', path: '/dashboard/admin/newsletter/subscribers', icon: 'subscribers' },
        { id: 'campaigns', label: 'Campaigns', path: '/dashboard/admin/newsletter/campaigns', icon: 'campaigns' },
        { id: 'create-campaign', label: 'Create Campaign', path: '/dashboard/admin/newsletter/create', icon: 'create' },
        { id: 'newsletter-settings', label: 'Newsletter Settings', path: '/dashboard/admin/newsletter/settings', icon: 'settings' },
        { id: 'portfolio-projects', label: 'Projects', path: '/dashboard/admin/portfolio/projects', icon: 'portfolio' },
        { id: 'portfolio-solutions', label: 'Solutions', path: '/dashboard/admin/portfolio/solutions', icon: 'solutions' },
        { id: 'portfolio-settings', label: 'Portfolio Settings', path: '/dashboard/admin/portfolio/settings', icon: 'settings' },
        { id: 'class-marks', label: 'Class Marks', path: '/dashboard/class-marks', icon: 'class-marks' },
      ];
    }
    
    if (isAdmin) {
      return [
        { id: 'dashboard', label: 'Dashboard', path: '/dashboard/main', icon: 'dashboard' },
        { id: 'my-applications', label: 'My Job Applications', path: '/dashboard/my-applications', icon: 'applications' },
        { id: 'training-management', label: 'Training Management', path: '/dashboard/admin/training/modules', icon: 'learning' },
        { id: 'student-progress', label: 'Student Progress', path: '/dashboard/admin/student-progress', icon: 'student-progress' },
        { id: 'announcements', label: 'Announcements', path: '/dashboard/admin/announcements', icon: 'announcements' },
        { id: 'portfolio-projects', label: 'Projects', path: '/dashboard/admin/portfolio/projects', icon: 'portfolio' },
        { id: 'portfolio-solutions', label: 'Solutions', path: '/dashboard/admin/portfolio/solutions', icon: 'solutions' },
        { id: 'class-marks', label: 'Class Marks', path: '/dashboard/class-marks', icon: 'class-marks' },
      ];
    }
    
    return [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard/main', icon: 'dashboard' },
      { id: 'my-applications', label: 'My Job Applications', path: '/dashboard/my-applications', icon: 'applications' },
      { id: 'requests', label: 'My Service Requests', path: '/dashboard/service-requests', icon: 'requests' },
      { id: 'new-request', label: 'New Request', path: '/dashboard/request-service', icon: 'new-request' },
      { id: 'trainings', label: 'My Learning', path: '/dashboard/trainings', icon: 'learning' },
      { id: 'class-marks', label: 'Class Marks', path: '/dashboard/class-marks', icon: 'class-marks' },
    ];
  };

  const menuItems = getMenuItems();

  // SVG Icons for navigation
  const getMenuIcon = (id: string) => {
    switch(id) {
      case 'dashboard':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9L12 3L21 9V20H3V9Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M9 20V12H15V20" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        );
      case 'requests':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M8 8H16M8 12H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'new-request':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        );
      case 'class-marks':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <rect x="2" y="3" width="20" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 6v2M8 12v2M8 18v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M16 6l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'profile':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        );
      case 'settings':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M19.4 15.05L18.1 16.35C17.7 16.75 17.7 17.45 18.1 17.85L19.4 19.15" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M4.6 4.85L5.9 6.15C6.3 6.55 7 6.55 7.4 6.15L8.7 4.85" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 2V3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 21V22" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M2 12H3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M21 12H22" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        );
      case 'learning':
      case 'training-management':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 12V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M4 14L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'announcements':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M8 11L12 8L16 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M8 15L12 12L16 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'message':
      case 'admin-contact':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'briefcase':
      case 'admin-jobs':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        );
      case 'applications':
      case 'admin-applications':
      case 'my-applications':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M16 21V5C16 3.89543 15.1046 3 14 3H10C8.89543 3 8 3.89543 8 5V21" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M12 11V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
      case 'blog':
      case 'admin-blog':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 7H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M8 11H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M8 15H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'support':
      case 'admin-support':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7443C15.6251 19.5894 14.0782 20.0573 12.5 20.1C11.1801 20.1034 9.87812 19.7951 8.7 19.2L3 21L4.8 15.3C4.2049 14.1219 3.89661 12.8199 3.9 11.5C3.94269 9.9218 4.4106 8.37486 5.25568 7.03248C6.10075 5.6901 7.28815 4.60554 8.7 3.9C9.87812 3.3049 11.1801 2.99661 12.5 3H13C15.0843 3.11499 17.053 3.99476 18.5291 5.47087C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'admin-users':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        );
      case 'admin-requests':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M8 2V6M16 2V6M3 10H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'admin-services':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'student-progress':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 12V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="12" cy="19" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M7 15L9 14M17 15L15 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'newsletter':
      case 'newsletter-dashboard':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M8 11L2 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M16 11L22 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="12" cy="15" r="1" fill="currentColor"/>
          </svg>
        );
      case 'subscribers':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M22 11L18 15M18 11L22 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'campaigns':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 12V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M7 9.5L12 7L17 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'create':
      case 'create-campaign':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'portfolio':
      case 'portfolio-projects':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="7" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M8 7V5C8 3.89543 8.89543 3 10 3H14C15.1046 3 16 3.89543 16 5V7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 12V16M10 14H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'solutions':
      case 'portfolio-solutions':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 12V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="12" cy="19" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        );
      case 'portfolio-settings':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M19.4 15.05L18.1 16.35C17.7 16.75 17.7 17.45 18.1 17.85L19.4 19.15" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M4.6 4.85L5.9 6.15C6.3 6.55 7 6.55 7.4 6.15L8.7 4.85" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 2V3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 21V22" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M2 12H3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M21 12H22" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        );
    }
  };

  // SVG Icons for notification types
  const getNotificationTypeIcon = (type: string) => {
    switch (type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="1.5" fill="none"/>
            <path d="M8 12L11 15L16 9" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9V12M12 15H12.01" stroke="#eab308" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z" stroke="#eab308" strokeWidth="1.5" fill="none"/>
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="1.5" fill="none"/>
            <path d="M15 9L9 15M9 9L15 15" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
      default: // info
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#3b82f6" strokeWidth="1.5" fill="none"/>
            <path d="M12 8V12M12 16H12.01" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
    }
  };

  // Role badge SVG icons
  const getRoleBadgeIcon = (role: string) => {
    switch (role) {
      case 'superadmin':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        );
      case 'admin':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        );
      default:
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        );
    }
  };

  return (
    <div className={`nt-dashboard-container ${sidebarOpen ? 'nt-sidebar-open' : 'nt-sidebar-closed'}`}>
      {isMobile && sidebarOpen && (
        <div className="nt-overlay" onClick={closeSidebar}></div>
      )}

      <aside className="nt-sidebar">
        <div className="nt-sidebar-header">
          <div className="nt-logo">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#0d47a1" strokeWidth="1.5" fill="none"/>
              <path d="M2 17L12 22L22 17" stroke="#0d47a1" strokeWidth="1.5" fill="none"/>
              <path d="M2 12L12 17L22 12" stroke="#0d47a1" strokeWidth="1.5" fill="none"/>
            </svg>
            {sidebarOpen && <h2 className="nt-logo-text">ZiTech Solutions</h2>}
          </div>
          <button className="nt-sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {sidebarOpen ? (
                <path d="M15 18L9 12L15 6" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
              ) : (
                <path d="M9 18L15 12L9 6" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
              )}
            </svg>
          </button>
        </div>

        <div className="nt-user-info">
          <div className="nt-user-avatar-large">
            {getInitials()}
          </div>
          {sidebarOpen && (
            <div className="nt-user-details">
              <h4 className="nt-user-name">{getFullName()}</h4>
              <p className="nt-user-email">{user?.email}</p>
              {user?.role && (
                <span className={`nt-user-role-badge nt-${user.role}`}>
                  {user.role === 'superadmin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'User'}
                </span>
              )}
            </div>
          )}
        </div>

        <nav className="nt-sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`nt-nav-item ${activeMenu === item.id ? 'nt-active' : ''}`}
              onClick={() => {
                navigate(item.path);
                closeSidebar();
              }}
            >
              <span className="nt-nav-icon">{getMenuIcon(item.icon)}</span>
              {sidebarOpen && <span className="nt-nav-label">{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="nt-sidebar-footer">
          <button className="nt-nav-item nt-logout" onClick={handleLogout}>
            <span className="nt-nav-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M21 12H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </span>
            {sidebarOpen && <span className="nt-nav-label">Logout</span>}
          </button>
        </div>
      </aside>

      <main className="nt-dashboard-main">
        <header className="nt-top-header">
          <div className="nt-header-left">
            <button className="nt-mobile-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 12H21M3 6H21M3 18H21" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <h1 className="nt-page-title">
              {menuItems.find(item => item.id === activeMenu)?.label || 'Dashboard'}
            </h1>
          </div>
          <div className="nt-header-right">
            <div className="nt-role-indicator">
              <span className={`nt-role-badge nt-${user?.role}`}>
                <span className="nt-role-icon">
                  {getRoleBadgeIcon(user?.role || 'user')}
                </span>
                {user?.role === 'superadmin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'User'}
              </span>
            </div>

            <div className="nt-notification-wrapper" ref={notificationRef}>
              <button 
                className="nt-notification-icon" 
                onClick={toggleNotifications}
                aria-label="Notifications"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="#666" strokeWidth="1.5" fill="none"/>
                  <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {unreadCount > 0 && (
                  <span className="nt-notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button>

              {notificationsOpen && (
                <div className="nt-notification-dropdown">
                  <div className="nt-notification-header">
                    <span className="nt-notification-title">Notifications</span>
                    {unreadCount > 0 && (
                      <button 
                        className="nt-notification-mark-all" 
                        onClick={markAllAsRead}
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>
                  <div className="nt-notification-list">
                    {loadingNotifications ? (
                      <div className="nt-notification-loading">Loading notifications...</div>
                    ) : notifications.length === 0 ? (
                      <div className="nt-notification-empty">No notifications</div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`nt-notification-item ${!notification.read ? 'nt-unread' : ''}`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="nt-notification-icon-wrapper">
                            {getNotificationTypeIcon(notification.type)}
                          </div>
                          <div className="nt-notification-content">
                            <div className="nt-notification-item-title">
                              {notification.title}
                              {!notification.read && (
                                <span className="nt-notification-unread-dot">
                                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="4" cy="4" r="4" fill="#0d47a1"/>
                                  </svg>
                                </span>
                              )}
                            </div>
                            <div className="nt-notification-item-message">
                              {notification.message}
                            </div>
                            <div className="nt-notification-item-time">
                              {getTimeAgo(notification.created_at)}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="nt-notification-footer">
                      <Link to="/dashboard/notifications" className="nt-notification-view-all">
                        View All Notifications
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="nt-user-menu-wrapper" ref={dropdownRef}>
              <div 
                className="nt-user-avatar-small" 
                onClick={toggleDropdown}
                aria-label="User menu"
              >
                {getInitials()}
              </div>

              {dropdownOpen && (
                <div className="nt-user-dropdown">
                  <div className="nt-dropdown-header">
                    <div className="nt-dropdown-avatar">
                      {getInitials()}
                    </div>
                    <div className="nt-dropdown-user-info">
                      <div className="nt-dropdown-user-name">{getFullName()}</div>
                      <div className="nt-dropdown-user-email">{user?.email}</div>
                      <span className={`nt-dropdown-role-badge nt-${user?.role}`}>
                        <span className="nt-dropdown-role-icon">
                          {getRoleBadgeIcon(user?.role || 'user')}
                        </span>
                        {user?.role === 'superadmin' ? 'Super Admin' : 
                         user?.role === 'admin' ? 'Admin' : 'User'}
                      </span>
                    </div>
                  </div>
                  <div className="nt-dropdown-divider"></div>
                  <div className="nt-dropdown-menu">
                    <button 
                      className="nt-dropdown-item"
                      onClick={() => {
                        setDropdownOpen(false);
                        navigate('/dashboard/profile');
                      }}
                    >
                      <span className="nt-dropdown-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                        </svg>
                      </span>
                      Profile
                    </button>
                    <button 
                      className="nt-dropdown-item"
                      onClick={() => {
                        setDropdownOpen(false);
                        navigate('/dashboard/settings');
                      }}
                    >
                      <span className="nt-dropdown-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M19.4 15.05L18.1 16.35C17.7 16.75 17.7 17.45 18.1 17.85L19.4 19.15" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M4.6 4.85L5.9 6.15C6.3 6.55 7 6.55 7.4 6.15L8.7 4.85" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M12 2V3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M12 21V22" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M2 12H3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M21 12H22" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                        </svg>
                      </span>
                      Settings
                    </button>
                  </div>
                  <div className="nt-dropdown-divider"></div>
                  <button 
                    className="nt-dropdown-item nt-dropdown-logout"
                    onClick={() => {
                      setDropdownOpen(false);
                      handleLogout();
                    }}
                  >
                    <span className="nt-dropdown-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                        <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M21 12H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="nt-page-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;